import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import AuditionSongService from '../../../services/auditionSongService';

const SongSearchModal = ({ isOpen, onClose, onSongSelected, voiceType, category, isLoading }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [characterRole, setCharacterRole] = useState('');
  const [showCharacterStep, setShowCharacterStep] = useState(false);

  useEffect(() => {
    if (searchTerm?.trim()?.length >= 2) {
      performSearch();
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  const performSearch = async () => {
    try {
      setIsSearching(true);
      const { data, error } = await AuditionSongService?.searchSongsForAudition(searchTerm);
      
      if (error) {
        console.error('Search error:', error);
        setSearchResults([]);
        return;
      }

      setSearchResults(data || []);
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSongSelect = (song) => {
    setSelectedSong(song);
    setShowCharacterStep(true);
  };

  const handleConfirmSelection = () => {
    if (selectedSong && characterRole?.trim()) {
      onSongSelected(selectedSong, characterRole?.trim());
    }
  };

  const handleBackToSearch = () => {
    setSelectedSong(null);
    setCharacterRole('');
    setShowCharacterStep(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-heading font-semibold">
                {showCharacterStep ? 'Select Character Role' : 'Search Songs'}
              </h3>
              <p className="text-blue-100 text-sm">
                {showCharacterStep 
                  ? `Assign character role for "${selectedSong?.title}"` 
                  : `${voiceType?.label} • ${category?.label}`
                }
              </p>
            </div>
            <Button
              variant="ghost"
              onClick={onClose}
              iconName="X"
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg"
            />
          </div>
        </div>

        <div className="p-6">
          {!showCharacterStep ? (
            /* Search Step */
            (<>
              {/* Search Input */}
              <div className="mb-6">
                <Input
                  type="text"
                  placeholder="Search for songs by title, musical, or composer..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full rounded-lg"
                />
                <p className="text-gray-500 text-xs mt-2">
                  Type at least 2 characters to start searching
                </p>
              </div>
              {/* Search Results */}
              <div className="max-h-96 overflow-y-auto">
                {isSearching && (
                  <div className="text-center py-8">
                    <Icon name="Loader" size={24} className="text-gray-400 animate-spin mx-auto mb-2" />
                    <p className="text-gray-500">Searching...</p>
                  </div>
                )}

                {!isSearching && searchTerm?.trim()?.length >= 2 && searchResults?.length === 0 && (
                  <div className="text-center py-8">
                    <Icon name="Search" size={32} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No songs found</p>
                    <p className="text-gray-400 text-sm">Try different search terms</p>
                  </div>
                )}

                {!isSearching && searchResults?.length > 0 && (
                  <div className="space-y-2">
                    {searchResults?.map((song) => (
                      <button
                        key={song?.id}
                        onClick={() => handleSongSelect(song)}
                        className="w-full bg-gray-50 hover:bg-gray-100 rounded-lg p-4 text-left transition-colors duration-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 truncate">
                              {song?.title}
                            </h4>
                            <div className="flex items-center space-x-2 text-sm text-gray-600 mt-1">
                              <span>{song?.musical?.title}</span>
                              <span>•</span>
                              <span>{song?.musical?.composer}</span>
                            </div>
                            {song?.musical?.lyricist && (
                              <p className="text-xs text-gray-500 mt-1">
                                Lyrics by {song?.musical?.lyricist}
                              </p>
                            )}
                          </div>
                          <Icon name="ChevronRight" size={20} className="text-gray-400" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>)
          ) : (
            /* Character Role Selection Step */
            (<>
              {/* Selected Song Info */}
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <Icon name="Music" size={20} className="text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{selectedSong?.title}</h4>
                    <p className="text-sm text-gray-600">
                      {selectedSong?.musical?.title} • {selectedSong?.musical?.composer}
                    </p>
                  </div>
                </div>
              </div>
              {/* Character Role Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Character Role *
                </label>
                <Input
                  type="text"
                  placeholder="e.g., Leading Man, Villain, Ensemble Member..."
                  value={characterRole}
                  onChange={(e) => setCharacterRole(e?.target?.value)}
                  className="w-full rounded-lg"
                />
                <p className="text-gray-500 text-xs mt-1">
                  Specify which character role this song represents for character-specific analysis
                </p>
              </div>
              {/* Action Buttons */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={handleBackToSearch}
                  iconName="ArrowLeft"
                  iconPosition="left"
                  className="rounded-lg"
                >
                  Back to Search
                </Button>

                <Button
                  variant="default"
                  onClick={handleConfirmSelection}
                  disabled={!characterRole?.trim() || isLoading}
                  iconName="Check"
                  iconPosition="left"
                  className="rounded-lg"
                >
                  {isLoading ? 'Assigning...' : 'Assign Song'}
                </Button>
              </div>
            </>)
          )}
        </div>
      </div>
    </div>
  );
};

export default SongSearchModal;